// @ts-strict-ignore
import * as React from 'react';
import { WarningFilled, ThunderboltFilled, CheckCircleFilled } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AutomationAutoStagedRollout, GoogleAppStoreSettings } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import { AppComponentProps, Nullable, StepRunComponentProps } from '../../typescriptHelpers';
import { convertAndroidStagedRolloutPercentage, displayStringForRolloutPercentage } from '../../helpers/displayHelpers';
import { getRolloutDaysToPercentageFromAutomation, releaseSettingsNeedPhasedReleaseWarning } from '../../helpers';
import { useAppPlatform } from '@/utils/hooks/helpers';
import { selectAutomationById, selectIsReleaseRollback } from '@/selectors/selectors';
import { labelForInAppUpdatePriority } from '@/helpers/appSettingsHelpers';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

interface GooglePlayStoreReleaseSettingsProps extends AppComponentProps {
    isRollback: boolean;
    isSubmitAutomationDisabledMissingDefaults: boolean;
    appDefaultSettings?: GoogleAppStoreSettings;
    releaseSettings?: GoogleAppStoreSettings;
    releaseId?: string;
    bordered?: boolean;
    labelStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    colon?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const GooglePlayStoreReleaseSettingsDescriptions: React.FC<GooglePlayStoreReleaseSettingsProps> = (
    props: GooglePlayStoreReleaseSettingsProps,
) => {
    const { releaseSettings } = props;

    const autoStagedAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customStagedRolloutEnabledAndActive = useSelector((state: RootState) => {
        const isEnabled = autoStagedAutomation?.enabled;

        if (props.releaseId) {
            const isRollback = selectIsReleaseRollback(state, props.releaseId);

            return isEnabled && !isRollback;
        }

        return isEnabled;
    });

    const stagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedAutomation);

    const platform = useAppPlatform(props);

    const stagedRolloutEnabled = !!releaseSettings && releaseSettings.stagedRelease === true;
    const stagedRolloutEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   null;
    const stagedRolloutPercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;
    const showStagedRolloutPercent =
        !!releaseSettings && releaseSettings.stagedRelease && !customStagedRolloutEnabledAndActive;

    const autoStagedRolloutSequence = [];

    if (stagedRolloutDaysToPercent) {
        stagedRolloutDaysToPercent.forEach((percent, i) => {
            autoStagedRolloutSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled =
        customStagedRolloutEnabledAndActive && !stagedRolloutEnabled;
    const needsSettingsWarning =
        props.isSubmitAutomationDisabledMissingDefaults ||
        customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled;
    const settingsWarningText =
        props.isSubmitAutomationDisabledMissingDefaults ?
            `Runway is unable to perform your scheduled submit automation because your Google
    Play staged rollout default is not set. Add release defaults to allow Runway to
    automatically submit as expected.`
        : customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled ?
            "Runway is unable to perform your configured custom staged rollout automation because staged rollout is not enabled. Change this default release method to staged rollout 'Yes' to allow Runway to perform your custom staged rollout."
        :   null;

    const needsSettingsConfirmation = stagedRolloutEnabled && customStagedRolloutEnabledAndActive;
    const confirmationText =
        "Keep your staged rollout set to 'Yes' to allow Runway to proceed with your custom staged rollout automation.";

    const showCustomStagedRollout =
        stagedRolloutEnabled && customStagedRolloutEnabledAndActive && stagedRolloutDaysToPercent;

    // warn if app release staged rollout settings differ from app default staged rollout settings
    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        props.isRollback,
        props.appDefaultSettings,
        releaseSettings,
    );

    return (
        <Descriptions
            column={1}
            labelStyle={props.labelStyle}
            contentStyle={props.contentStyle}
            colon={props.colon}
            bordered={props.bordered}
        >
            <Descriptions.Item label="Staged rollout enabled">
                <Space direction="vertical">
                    {stagedRolloutEnabledText ?? <Text type="secondary">Not set</Text>}
                    {needsSettingsWarning && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>{settingsWarningText}</Text>
                        </Space>
                    )}
                    {needsSettingsConfirmation && (
                        <Space align="start">
                            <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                            <Text style={{ color: antGrey.grey7 }}>{confirmationText}</Text>
                        </Space>
                    )}
                    {needsPhasedReleaseWarning && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>
                                Staged rollout has been disabled for this release because it&apos;s a rollback.
                            </Text>
                        </Space>
                    )}
                </Space>
            </Descriptions.Item>
            {showStagedRolloutPercent && (
                <Descriptions.Item label="Initial staged rollout percentage">
                    {stagedRolloutPercent ?? <Text type="secondary">Not set</Text>}
                </Descriptions.Item>
            )}
            {showCustomStagedRollout && (
                <Descriptions.Item label="Custom staged rollout">
                    <Space>
                        <ThunderboltFilled style={{ color: runwayColors.primary }} />
                        <Text style={{ color: antGrey.grey7 }}>Automated custom staged rollout is enabled.</Text>
                        <TooltipInfoIcon
                            hasCustomContent
                            tooltipContent={
                                <Space direction="vertical">
                                    <Space direction="vertical" size={0}>
                                        {autoStagedRolloutSequence.map((day, i) => {
                                            return (
                                                <Text style={{ color: 'white' }} key={i}>
                                                    {day}
                                                </Text>
                                            );
                                        })}
                                    </Space>
                                    <Text style={{ color: 'white' }}>
                                        You can configure your custom staged rollout from automation settings.
                                    </Text>
                                </Space>
                            }
                        />
                    </Space>
                </Descriptions.Item>
            )}
            <Descriptions.Item label="In-app update priority">
                {labelForInAppUpdatePriority(releaseSettings?.inAppUpdatePriority)}
            </Descriptions.Item>
        </Descriptions>
    );
};

export default GooglePlayStoreReleaseSettingsDescriptions;
